/**
 * auto-captions-eta — per-stage ETA helper for the transcribe/translate/burn
 * progress bars.
 *
 * Whisper Large on a 5-minute clip used to stare at a static bar for ~2 minutes
 * because the worker reports `chunk_done` in coarse chunks, not in continuous
 * fractions. The fix is two-pronged:
 *   1. Estimate remaining time from elapsedMs / frac inside each stage.
 *   2. EMA-smooth the estimate so it doesn't jitter every progress packet.
 *
 * Exposed as `window.__acETA`:
 *
 *   beginStage(key)           — reset timer + EMA window for a stage
 *   update(key, frac) -> str  — returns "~30s left" / "~2m 15s left" / null
 *                                when frac < 0.05 (too noisy to estimate).
 *                                Also returns null when beginStage was never
 *                                called for the key (caller-side safety).
 *   endStage(key)             — drop the stage's state.
 *   format(secs) -> string    — "1m 23s" / "45s" / "~2h 15m".
 *
 * Algorithm:
 *   elapsedMs / frac = totalEst
 *   remainingMs      = totalEst - elapsedMs  (== totalEst * (1 - frac))
 *
 * EMA window: last 5 samples, exponential weight 2/(N+1) = 1/3, so the most
 * recent estimate counts ~33 %, older ones decay quickly. Good enough to absorb
 * a chunk_done spike from 0.55 → 0.85 without the ETA collapsing from "1m left"
 * to "10s left" then back to "50s left" on the next normal sample.
 *
 * Pure-function-friendly: the only external dep is the clock — window.__activeNow
 * when the page has it (timbrica-core.js), else Date.now() — mockable in tests
 * by passing an explicit `nowMs` to update / beginStage).
 */
(function () {
    'use strict';

    var EMA_WINDOW = 5;
    var EMA_ALPHA  = 2 / (EMA_WINDOW + 1); // ≈ 0.333
    var MIN_FRAC   = 0.05;                  // below this, estimate is too noisy

    /** stageKey → { startMs, emaMs (smoothed remainingMs, or null), samples } */
    var stages = Object.create(null);

    /* The default clock is ACTIVE time (window.__activeNow, timbrica-core.js):
     * wall time minus the stretches the page could not run — a tab the
     * browser froze in the background, a system asleep. On wall time the next
     * advance after a frozen hour arrives with Δt = one hour, its EMA weight is
     * 1 − e^(−Δt/τ) ≈ 1, and the rate collapses to «half a per cent an hour»:
     * the /video-fps copy of this arithmetic printed «около 30 ч» at 89 % of a
     * run that had simply stood in a background tab (prod 26.09.2026). The
     * choice is made once, at the first call, so one stage never mixes two
     * clocks; an explicit `ms` (tests) always wins. */
    var clockFn = null;
    function now(ms) {
        if (typeof ms === 'number' && isFinite(ms)) return ms;
        if (!clockFn) {
            clockFn = (typeof window !== 'undefined' && typeof window.__activeNow === 'function')
                ? function () { return window.__activeNow(); }
                : function () { return Date.now(); };
        }
        return clockFn();
    }

    function beginStage(key, nowMs) {
        if (typeof key !== 'string' || !key) return;
        stages[key] = {
            startMs: now(nowMs),
            emaMs:   null,
            samples: 0,
        };
    }

    function endStage(key) {
        if (typeof key !== 'string' || !key) return;
        delete stages[key];
    }

    /**
     * Update progress for a stage and return a human-readable "~Xs left" string
     * to append to the progress label, or null when no estimate is available.
     *
     * The label suffix template is fetched lazily so the I18N table doesn't
     * need to exist at module-load time.
     */
    function update(key, frac, nowMs) {
        if (typeof key !== 'string' || !key) return null;
        var s = stages[key];
        if (!s) return null;
        if (typeof frac !== 'number' || !isFinite(frac)) return null;
        if (frac <= MIN_FRAC) return null;
        if (frac >= 1) {
            // At 100 %, "left" is meaningless. Caller's endStage will clean up.
            return null;
        }

        var elapsed = now(nowMs) - s.startMs;
        if (elapsed <= 0) return null;

        var totalEst    = elapsed / frac;
        var remaining   = Math.max(0, totalEst - elapsed); // === totalEst * (1 - frac)

        // EMA-smooth. First sample seeds the average; later samples blend in.
        if (s.emaMs == null || s.samples === 0) {
            s.emaMs = remaining;
        } else {
            s.emaMs = EMA_ALPHA * remaining + (1 - EMA_ALPHA) * s.emaMs;
        }
        s.samples += 1;

        var secs = Math.round(s.emaMs / 1000);
        if (secs <= 0) return null;

        return applySuffix(secs);
    }

    /**
     * Format seconds as "1m 23s" / "45s" / "~2h 15m" / "~1d 3h".
     * Rules:
     *   < 60s            → "45s"          (no tilde, precise)
     *   < 1h             → "1m 23s"       (no tilde)
     *   < 24h            → "~2h 15m"      (tilde — minutes-grain only)
     *   ≥ 24h            → "~1d 3h"       (tilde — hours-grain only)
     *
     * Always emits at least one component. Defensive against negative / NaN.
     */
    function format(secs) {
        if (typeof secs !== 'number' || !isFinite(secs) || secs < 0) return '0s';
        secs = Math.round(secs);
        if (secs < 60) return secs + 's';
        var m = Math.floor(secs / 60);
        var s = secs % 60;
        if (m < 60) return m + 'm' + (s ? ' ' + s + 's' : '');
        var h = Math.floor(m / 60);
        m = m % 60;
        if (h < 24) return '~' + h + 'h' + (m ? ' ' + m + 'm' : '');
        var d = Math.floor(h / 24);
        h = h % 24;
        return '~' + d + 'd' + (h ? ' ' + h + 'h' : '');
    }

    /**
     * Wrap `format(secs)` in the localized "· ~:duration left" suffix.
     * Falls back to a hard-coded English template if I18N hasn't loaded.
     */
    function applySuffix(secs) {
        var duration = format(secs);
        var tpl;
        if (typeof window !== 'undefined' && window.__acI18n && window.__acI18n.eta_label_suffix) {
            tpl = window.__acI18n.eta_label_suffix;
        } else {
            tpl = ' · ~:duration left';
        }
        return tpl.replace(':duration', duration);
    }

    /**
     * Honest, NON-BALLOONING ETA from chunk completions.
     *
     * `update(key, frac)` extrapolates elapsed/frac — which INFLATES upward
     * while a single slow chunk is in flight: the caller's `frac` is creep-
     * capped (the bar can only creep to ~92 % of the next slot), so as wall
     * time passes with `frac` plateaued, `elapsed/frac` keeps growing and the
     * "~N min left" balloons. On a weak device this showed "осталось ~29 мин"
     * for a 50 s clip (valeriitaglin, 2026-06-20) — a number large enough that
     * users quit before it ever finished, even though the real remaining was a
     * few minutes.
     *
     * This estimator instead uses the average wall-time of COMPLETED chunks ×
     * the chunks left, minus the in-flight chunk's elapsed (capped at one avg so
     * it never goes negative). Between completions it ticks DOWN smoothly and
     * never balloons; when a slow chunk finally finishes, avgChunkMs rises and
     * the estimate steps to the new, honestly-slower value.
     *
     *   avgChunkMs — (lastChunkDoneMs - startMs) / doneChunks   [completed only]
     *   remaining  — avgChunkMs * chunksLeft - min(curChunkElapsedMs, avgChunkMs)
     *
     * Returns null until at least one chunk has completed (before that any
     * estimate is pure noise — the label just shows "Recognizing…").
     */
    function updateChunks(key, avgChunkMs, chunksLeft, curChunkElapsedMs) {
        if (typeof key !== 'string' || !key) return null;
        var s = stages[key];
        if (!s) return null;
        if (typeof avgChunkMs !== 'number' || !isFinite(avgChunkMs) || avgChunkMs <= 0) return null;
        if (typeof chunksLeft !== 'number' || !isFinite(chunksLeft) || chunksLeft < 0) return null;
        var curElapsed = (typeof curChunkElapsedMs === 'number' && isFinite(curChunkElapsedMs)) ? curChunkElapsedMs : 0;
        var remaining = Math.max(0, avgChunkMs * chunksLeft - Math.min(curElapsed, avgChunkMs));

        if (s.emaMs == null || s.samples === 0) {
            s.emaMs = remaining;
        } else {
            s.emaMs = EMA_ALPHA * remaining + (1 - EMA_ALPHA) * s.emaMs;
        }
        s.samples += 1;

        var secs = Math.round(s.emaMs / 1000);
        if (secs <= 0) return null;
        return applySuffix(secs);
    }

    /**
     * Honest ETA for a CONTINUOUS progress signal (0..1) that can PLATEAU —
     * an FFmpeg-WASM transcode, a long single-threaded encode. `updateChunks`
     * needs discrete chunk completions (Whisper); this is its equivalent for a
     * fraction that creeps and then stalls.
     *
     * Why not `update()`: it recomputes elapsed/frac on EVERY call, so while
     * the fraction is frozen and wall time keeps passing the estimate GROWS —
     * the same balloon that made users quit on auto-captions. Measured on the
     * slideshow transcode signal (progress frozen at 0.70): the naive formula
     * climbed 5.0 → 6.5 → 8.0 → 9.5 → 11.0 min purely from waiting.
     *
     * Rule here: speed is measured ONLY across real advances; a plateau can
     * only count the estimate DOWN. A genuine slowdown still raises the number,
     * but on EVIDENCE (the next advance drags the EMA rate down), never from
     * waiting alone.
     *
     *   on advance : rate = Δfrac / Δt  → EMA → remaining = (1 - frac) / rate
     *   on plateau : remaining -= elapsed since the last computation
     *
     * Returns null until the first measured advance (any earlier number is
     * noise), and null once a countdown is exhausted — at that point we have
     * underestimated and showing a fresh number would just be a new lie.
     */
    /* Rate estimator constants — chosen by replaying REAL export traces
     * (2026-09-25, slideshow: a weak machine emulated with --disable-gpu + CPU×4/×6,
     * 302 and 403 s; three ~35 s exports) through nine candidates and scoring
     * what a person would SEE. The previous sample-count EMA (α = 1/3 per
     * advance) sat within ×1.5 of the truth 34 % of the time on a weak machine,
     * showed «≈69 мин» for a 5-minute export at 2 %, and jumped UP by ≥ 1.5×
     * six times a minute («10 → 2 → 9 → 1 → 7 мин») — the texture of a stuck
     * export, and the cancel-and-restart it provokes (bug report 24.09). This
     * one: 75 % within ×1.5, worst over-promise ×3.8, 0.07 big upward jumps a
     * minute; on short exports 52 % against 14 %.
     *   RATE_TAU_MS   continuous-time EMA of the measured rate: the weight of an
     *                 advance is 1 − e^(−Δt/τ), so a burst of tiny fast advances
     *                 cannot outvote a minute of steady work (a per-SAMPLE EMA
     *                 can, and did) — τ 10/15/20/30 s measured, 20 best overall;
     *   RATE_GATE_*   no number before 3 % of the stage AND 5 s of it — before
     *                 that the rate is noise (the 69-minute start);
     *   RATE_RAISE    a new estimate ABOVE the shown one replaces it only past
     *                 ×1.25; below that the shown one keeps counting down (a real
     *                 slowdown still shows, a wobble does not). */
    var RATE_TAU_MS = 20000;
    var RATE_GATE_FRAC = 0.03;
    var RATE_GATE_MS = 5000;
    var RATE_RAISE = 1.25;

    function updateRate(key, frac, nowMs) {
        if (typeof key !== 'string' || !key) return null;
        var s = stages[key];
        if (!s) return null;
        if (typeof frac !== 'number' || !isFinite(frac)) return null;
        if (frac >= 1) return null;
        var t = now(nowMs);

        // First sighting of this stage's signal — seed the baseline only.
        if (s.rateLastFrac == null) {
            s.rateF0       = frac;
            s.rateT0       = t;
            s.rateLastFrac = frac;
            s.rateLastMs   = t;
            s.rateEma      = null;   // fraction per ms
            s.rateRemMs    = null;   // last computed remaining
            s.rateCalcMs   = t;
            s.rateOutMs    = null;   // what is REPORTED (after the raise hysteresis)
            s.rateOutAt    = t;
            s.rateGateOk   = false;
            return null;
        }

        var dFrac = frac - s.rateLastFrac;
        if (dFrac > 1e-6) {
            var dMs  = Math.max(1, t - s.rateLastMs);
            if (s.rateEma == null) {
                // First advance: the stage's own average so far, not one interval.
                s.rateEma = (frac - s.rateF0) / Math.max(1, t - s.rateT0);
            } else {
                var a = 1 - Math.exp(-dMs / RATE_TAU_MS);
                s.rateEma = a * (dFrac / dMs) + (1 - a) * s.rateEma;
            }
            s.rateLastFrac = frac;
            s.rateLastMs   = t;
            if (s.rateEma > 0) s.rateRemMs = (1 - frac) / s.rateEma;
            s.rateCalcMs = t;
        } else {
            // Plateau — never re-extrapolate, just spend the wait.
            if (s.rateRemMs != null) {
                s.rateRemMs  = Math.max(0, s.rateRemMs - (t - s.rateCalcMs));
                s.rateCalcMs = t;
            }
        }

        // What is reported counts down by itself; a higher estimate replaces it
        // only when it is clearly higher (RATE_RAISE), a lower one always.
        var out = s.rateOutMs;
        if (out != null) out = Math.max(0, out - (t - s.rateOutAt));
        var rem = s.rateRemMs;
        if (rem != null && (out == null || rem <= out || rem >= out * RATE_RAISE)) out = rem;
        s.rateOutMs = out;
        s.rateOutAt = t;

        if (!s.rateGateOk && frac - s.rateF0 >= RATE_GATE_FRAC && t - s.rateT0 >= RATE_GATE_MS && frac > MIN_FRAC) s.rateGateOk = true;
        if (out == null || !s.rateGateOk) return null;
        var secs = Math.round(out / 1000);
        if (secs <= 0) return null;
        return applySuffix(secs);
    }

    /**
     * Raw remaining seconds for a stage driven by `updateRate`, or null when
     * there is nothing honest to report. Lets a caller with its OWN localized
     * wording (slideshow-maker has eta_seconds/eta_minutes in 9 locales) reuse
     * the estimator. Obeys the same evidence gate as `updateRate` — until
     * 2026-09-25 it did not, and the slideshow printed «≈69 мин» at 2 %.
     */
    function remainingSec(key) {
        if (typeof key !== 'string' || !key) return null;
        var s = stages[key];
        if (!s || s.rateOutMs == null || !s.rateGateOk) return null;
        var secs = Math.round(s.rateOutMs / 1000);
        return secs > 0 ? secs : null;
    }

    /** Test-only: expose the internal stage map without freezing it. */
    function _stages() { return stages; }

    var api = {
        beginStage:   beginStage,
        update:       update,
        updateChunks: updateChunks,
        updateRate:   updateRate,
        remainingSec: remainingSec,
        endStage:     endStage,
        format:       format,
        _stages:      _stages,
    };

    if (typeof window !== 'undefined') window.__acETA = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
